import {Badge, DropdownItem, DropdownSearchItem, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {useMemo, useState} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import type {EagColumnState} from '../../../../types/EagColumnState';
import {matchesColumnSearch} from '../../../../utils/matchesColumnSearch';
import {normalizeSearchText} from '../../../../utils/normalizeSearchText';

export type FindColumnEntry = {
  key: string;
  headerName: string;
  /** Display label of the column's group — searched like the name and shown next to it. */
  groupLabel?: string;
};

type FindColumnDropdownProps = {
  columns: FindColumnEntry[];
  columnStates: EagColumnState[];
  onSelect: (columnKey: string) => void;
} & TestIdProps;

type FindColumnItemSuffixProps = {
  columnKey: string;
  groupLabel?: string;
  isHidden: boolean;
} & TestIdProps;

/** Muted group label and, for a hidden column, the "Hidden" badge, trailing the item label. */
function FindColumnItemSuffix(props: FindColumnItemSuffixProps) {
  const t = useTranslationContext();

  return (
    <span className="flex items-center gap-2">
      <Show when={isNotNilOrEmpty(props.groupLabel) ? props.groupLabel : null}>
        {(groupLabel) => (
          <span
            className="max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap text-[11px] text-[#6B778C]"
            title={groupLabel}
            data-testid={suffixTestId(`findColumn-group-${props.columnKey}`, props)}
          >
            {groupLabel}
          </span>
        )}
      </Show>
      <Show when={props.isHidden}>
        <Badge
          value={t('page.datagrid.labels.hidden')}
          size="small"
          variant="subtle"
          colorScheme="neutral"
          data-testid={suffixTestId(`findColumn-hiddenBadge-${props.columnKey}`, props)}
        />
      </Show>
    </span>
  );
}

export function FindColumnDropdown(props: FindColumnDropdownProps) {
  const t = useTranslationContext();
  const [search, setSearch] = useState<string | null>(null);

  const hiddenKeys = useMemo(
    () => new Set(props.columnStates.filter((cs) => cs.hidden).map((cs) => cs.key)),
    [props.columnStates]
  );

  // Name OR group label, case- and diacritics-insensitive: typing a platform
  // name lists every column of that group (T20-142344).
  const normalizedTerm = normalizeSearchText((search ?? '').trim());
  const filtered = normalizedTerm
    ? props.columns.filter((col) =>
        matchesColumnSearch(col.headerName, col.groupLabel, normalizedTerm)
      )
    : props.columns;

  return (
    <>
      <DropdownSearchItem
        value={search}
        onChange={setSearch}
        isAutoFocused
        placeholder={t('page.datagrid.labels.findColumnPlaceholder')}
        data-testid={suffixTestId('findColumn-search', props)}
      />
      {filtered.length === 0 ? (
        <div
          className="text-sm py-6 text-center"
          style={{color: '#7A869A'}}
          data-testid={suffixTestId('findColumn-empty', props)}
        >
          {t('page.datagrid.labels.noColumnsFound')}
        </div>
      ) : (
        filtered.map((col) => {
          const isHidden = hiddenKeys.has(col.key);
          const hasSuffix = isHidden || isNotNilOrEmpty(col.groupLabel);
          return (
            <DropdownItem
              key={col.key}
              label={col.headerName}
              suffix={
                hasSuffix ? (
                  <FindColumnItemSuffix
                    columnKey={col.key}
                    groupLabel={col.groupLabel}
                    isHidden={isHidden}
                    data-testid={props['data-testid']}
                  />
                ) : undefined
              }
              onClick={() => props.onSelect(col.key)}
              data-testid={suffixTestId(`findColumn-item-${col.key}`, props)}
            />
          );
        })
      )}
    </>
  );
}
